import React from 'react';

import { getField } from 'apps/auth/components/form-multi/utils';
import UsaHighRiskCountryOptInModal from 'apps/auth/components/send/optIn/UsaHighRiskCountryOptInModal';
import { CREATE_REQUEST, SEND_TO } from 'apps/auth/constants/Constants';
import { AUTH_GLOBAL_CARD_INFO, AUTH_PAYSYSTEM_CHECK } from 'apps/auth/constants/UrlConstants';
import { apiFetchData, changeBrowserUrlWithOutReload } from 'apps/auth/helpers/api';
import { logWarn } from 'apps/auth/helpers/clientLogger';
import { validOnMod10 } from 'apps/auth/helpers/utils';
import { IS_SHOW_SHORT_VALUE, SET_IS_FOCUSED, SET_SHOW_CVV, SET_SHOW_EXP_DATE } from 'apps/auth/hooks/useReducer';

export class CardService {
    #isRedirectByBinInfo;
    #formName;
    #binInfo;
    #binExtraName;
    #redirectByBinUrl;
    #countryIdForCard;
    #countryA2To;
    #countryA2From;
    #invalidCardError;
    #actionType;
    #removeFieldError;
    #setFieldError;
    #setNewForm;
    #setNewFields;
    #updateExtraByName;
    #addExtras;
    #setExtra;
    #toggleSpinner;
    #updateMessages;
    #updatePageMessages;

    #isGlobalFlow;
    #isBonusWithdraw = false;
    #typeOfTransfer = '';
    #visibleFields;

    constructor(formName, binExtraName, redirectByBinUrl, countryIdForCard, actionType, countryA2To, countryA2From, invalidCardError) {
        this.#formName = formName;
        this.#binExtraName = binExtraName;
        this.#redirectByBinUrl = redirectByBinUrl;
        this.#countryIdForCard = countryIdForCard;
        this.#countryA2To = countryA2To;
        this.#countryA2From = countryA2From;
        this.#actionType = actionType;
        this.#invalidCardError = invalidCardError;
    }

    visibleFields(visibleFields) {
        this.#visibleFields = visibleFields;
    }

    isGlobalFlow(isGlobalFlow) {
        this.#isGlobalFlow = isGlobalFlow;
    }

    isBonusWithdraw(isBonusWithdraw) {
        this.#isBonusWithdraw = isBonusWithdraw;
    }

    getTypeQueryParamForUrl() {
        return this.#typeOfTransfer ? `type=${this.#typeOfTransfer}` : '';
    }

    setTypeOfTransfer(typeOfTransfer) {
        this.#typeOfTransfer = typeOfTransfer;
    }

    setRedirectByBinInfo(redirectByBinInfo) {
        this.#isRedirectByBinInfo = redirectByBinInfo;
    }

    setAppAction(toggleSpinner, updateMessages) {
        this.#toggleSpinner = toggleSpinner;
        this.#updateMessages = updateMessages;
    }

    setFormAction(removeFieldError, setFieldError, setNewForm, setNewFields) {
        if (this.#removeFieldError === undefined) {
            this.#removeFieldError = removeFieldError;
            this.#setFieldError = setFieldError;
            this.#setNewForm = setNewForm;
            this.#setNewFields = setNewFields;
        }
    }

    setExtraAction(updateExtraByName, addExtras, setExtra) {
        if (this.#updateExtraByName === undefined) {
            this.#updateExtraByName = updateExtraByName;
            this.#addExtras = addExtras;
            this.#setExtra = setExtra;
        }
    }

    setMessageActions(updatePageMessages) {
        if (this.#updatePageMessages === undefined) {
            this.#updatePageMessages = updatePageMessages;
        }
    }

    checkMod10(value, panField, defaultErrorMessage) {
        if (!value || (value && !validOnMod10(value))) {
            this.#setFieldError(this.#formName, panField.name, defaultErrorMessage);
        }
    }

    checkPaymentService(panValue, panField, fields, controller, binInfo) {
        let url = AUTH_PAYSYSTEM_CHECK + `?${this.getTypeQueryParamForUrl()}`;
        if (binInfo?.warningAllowed) {
            url = `${url}&warningAllowed=true`;
        }
        apiFetchData(url, this.#getFieldsForgetPaymentSystem(panValue, fields), controller)
            .then((json) => {
                this.#binInfo = json.extra?.binInfo;
                if (json.extra) {
                    this.#addExtras(json.extra);
                }
                if (json.extra?.showRedirectByBinPopup) {
                    this.#updatePageMessages(json.messages).then(() => {
                        this.#addExtras(json.extra);
                        this.#updateExtraByName(this.#binExtraName, this.#binInfo);
                    });
                } else if (json.extra?.showCardErrorPopUp || json.extra?.cardTypeError) {
                    this.#updatePageMessages(json.messages).then(() => {
                        this.#addExtras(json.extra);
                        this.#updateExtraByName(this.#binExtraName, this.#binInfo);
                    });
                } else {
                    if (json.errors && Object.keys(json.errors).length > 0) {
                        this.#updatePageMessages(json.messages);
                        Object.keys(json.errors).forEach((name) => this.#setFieldError(this.#formName, name, json.errors[name]));
                    } else if (this.#binInfo?.error) {
                        this.#setFieldError(this.#formName, panField.name, this.#binInfo.error);
                    } else if (panField.isError && panField.errorMessage) {
                        this.#setFieldError(this.#formName, panField.name, null);
                    }
                    const form = json.forms[this.#formName];
                    if (form?.fields) {
                        this.#setNewForm(this.#formName, form?.fields);
                    }
                    this.#checkBinInfo(this.#binInfo, panField, panValue);
                }
            })
            .catch((e) => logWarn(e));
    }

    getCardInfoForGlobalProvider(panValue, panField, fields, controller, popupRef, popupFullwidthRef, modalRef) {
        apiFetchData(
            `${AUTH_GLOBAL_CARD_INFO}?isBonusWithdraw=${this.#isBonusWithdraw}&${this.getTypeQueryParamForUrl()}`,
            this.#getFieldsForgetPaymentSystem(panValue, fields),
            controller,
        )
            .then((json) => {
                this.#binInfo = json.extra?.binInfo;
                const toCountry = json?.extra?.toCountry;
                if (json.extra) {
                    this.#addExtras(json.extra);
                }
                if (toCountry?.migrationOnly) {
                    popupRef.current?.close();
                    popupFullwidthRef.current?.open(
                        <UsaHighRiskCountryOptInModal
                            modalRef={modalRef}
                            popupFullwidthRef={popupFullwidthRef}
                            country={toCountry}
                            title={toCountry?.migrationOnlyTitle}
                            subtitle={toCountry?.migrationOnlySubTitle}
                        />,
                    );
                    this.#toggleSpinner(false);
                    return;
                }
                if (json.errors) {
                    this.#updatePageMessages(json.messages);
                    Object.keys(json.errors).forEach((name) => this.#setFieldError(this.#formName, name, json.errors[name]));
                }
                if (json.forms && json.forms[this.#formName]) {
                    this.#updateForGlobalProvider(json.extra?.redirectUrl, json.forms[this.#formName], popupRef);
                }
            })
            .catch((e) => logWarn(e));
    }

    #checkBinInfo = (binInfo = {}, panField = {}, panValue = '') => {
        if (this.#redirectByBinUrl && binInfo.countryId && 0 !== binInfo.countryId && this.#countryIdForCard !== binInfo.countryId && panValue.length > 9) {
            this.#redirectByBin(panValue, fields);
        } else if (binInfo.neadCheckMod10) {
            if (!validOnMod10(panValue) && !binInfo.error && panValue.length >= 16) {
                this.#setFieldError(this.#formName, panField.name, this.#invalidCardError);
            } else {
                this.#updateExtraByName(this.#binExtraName, binInfo);
                if (!binInfo?.error) {
                    this.#removeFieldError(this.#formName, panField.name);
                }
            }
        } else {
            this.#updateExtraByName(this.#binExtraName, binInfo);
        }
    };

    #getFieldsForgetPaymentSystem = (panValue, fields) => {
        const newFields = { ...fields };
        const check = (this.#actionType === SEND_TO || this.#actionType === CREATE_REQUEST).toString();
        newFields.pan = getField('pan', panValue);
        newFields.redirectByBinInfo = getField('redirectByBinInfo', this.#isRedirectByBinInfo);
        newFields.actionType = getField('actionType', this.#actionType);
        newFields.checkPaymentSystem = getField('checkPaymentSystem', check);
        newFields.checkPrepaidCard = getField('checkPrepaidCard', check);
        newFields.paySystem = getField('paySystem', this.#actionType === SEND_TO ? fields?.toProviderIcon?.value : fields?.fromProviderIcon?.value);
        newFields.countryA2From = getField('countryA2From', this.#countryA2From);
        newFields.countryA2To = getField('countryA2To', this.#countryA2To);
        return newFields;
    };

    #redirectByBin = (panValue, fields) => {
        this.#toggleSpinner(true);
        const params = { ...fields, bin: getField('bin', panValue) };
        apiFetchData(this.#redirectByBinUrl, params)
            .then((json) => {
                if (json) {
                    if (json.messageBodyMessages) {
                        this.#updateMessages(json.messageBodyMessages);
                    }
                    if (json.messages) {
                        this.#updatePageMessages(json.messages);
                    }
                    if (json.extra) {
                        this.#addExtras(json.extra);
                    }
                    const form = json.forms[this.#formName];
                    if (form?.fields) {
                        this.#setNewFields(this.#formName, form.fields);
                    }
                    changeBrowserUrlWithOutReload(json.redirectTo);
                }
                this.#toggleSpinner(false);
            })
            .catch((e) => {
                logWarn(e);
                this.#toggleSpinner(false);
            });
    };

    redirectByBin(redirectUrl, fields, popupRef, popupFullwidthRef, modalRef) {
        let redirectUrlWithParams = redirectUrl + `?resetComponent=true&${this.getTypeQueryParamForUrl()}`;
        this.#toggleSpinner(true);
        apiFetchData(redirectUrlWithParams, fields)
            .then((json) => {
                const toCountry = json?.extra?.toCountry;
                if (toCountry?.migrationOnly) {
                    popupRef.current?.close();
                    popupFullwidthRef.current?.open(
                        <UsaHighRiskCountryOptInModal
                            modalRef={modalRef}
                            popupFullwidthRef={popupFullwidthRef}
                            country={toCountry}
                            title={toCountry?.migrationOnlyTitle}
                            subtitle={toCountry?.migrationOnlySubTitle}
                        />,
                    );
                    return;
                }
                changeBrowserUrlWithOutReload(redirectUrlWithParams);
                popupRef.current?.close();
                if (json.extra) {
                    if (json.messages) {
                        this.#updatePageMessages(json.messages);
                    }
                    if (json.messageBodyMessages) {
                        this.#updateMessages(json.messageBodyMessages);
                    }
                    this.#setExtra({ [this.#binExtraName]: this.#binInfo, ...json.extra }).then(() => {
                        if (this.#binInfo?.error) {
                            const newBinInfo = { ...this.#binInfo, error: null };
                            this.#updateExtraByName(this.#binExtraName, newBinInfo);
                        }
                        Object.values(json.forms).forEach((form) => {
                            if (form && form.name) {
                                this.#setNewForm(form.name, form.fields, form.action);
                            }
                        });
                    });
                }
            })
            .finally(() => {
                this.#toggleSpinner(false);
            });
    }

    #updateForGlobalProvider(redirectUrl, form, popupRef) {
        changeBrowserUrlWithOutReload(redirectUrl);
        popupRef.current?.close();
        this.#toggleSpinner(false);
        this.#setNewForm(form.name, form.fields, form.action, this.#visibleFields);
    }

    popupText(text1, text2) {
        return (
            <>
                <p>{text1}</p>
                <p>{text2}</p>
            </>
        );
    }
}

export const onFocusHandler = (dispatch, expiredDate, cvv, state) => {
    if (expiredDate && state.isShowExpDate) {
        dispatch({ type: SET_SHOW_EXP_DATE, isShowExpDate: false });
    }
    if (cvv && state.isShowCvv) {
        dispatch({ type: SET_SHOW_CVV, isShowCvv: false });
    }
    dispatch({ type: SET_IS_FOCUSED, isFocused: true });
    dispatch({ type: IS_SHOW_SHORT_VALUE, isShowShortValue: false });
};
export const onBlurHandler = (dispatch, expiredDate, cvv, state, isError) => {
    if (expiredDate && !state.isShowExpDate) {
        dispatch({ type: SET_SHOW_EXP_DATE, isShowExpDate: true });
    }
    if (cvv && !state.isShowCvv) {
        dispatch({ type: SET_SHOW_CVV, isShowCvv: true });
    }
    if (!isError) {
        dispatch({ type: SET_IS_FOCUSED, isFocused: false });
    }
    dispatch({ type: IS_SHOW_SHORT_VALUE, isShowShortValue: true });
};
export const arrowButtonClick = (e, dispatch, state, expiredDate, cvv) => {
    e.preventDefault();
    dispatch({ type: SET_SHOW_EXP_DATE, isShowExpDate: !!expiredDate });
    dispatch({ type: SET_SHOW_CVV, isShowCvv: !!cvv });
    dispatch({ type: SET_IS_FOCUSED, isFocused: false });
};

export const isExpiryValid = (value, now = new Date()) => {
    const m = value.match(/^(\d{2})\/(\d{2})$/);
    if (!m) return false;
    const mm = parseInt(m[1], 10);
    const yy = parseInt(m[2], 10);
    if (mm < 1 || mm > 12) return false;
    const year = 2000 + yy;
    const curYear = now.getFullYear();
    const curMonth = now.getMonth() + 1;
    return year > curYear || (year === curYear && mm >= curMonth);
};
